<template>
  <v-list-item
    density="compact"
    :id="
      shouldShowAddIcon ? 'product_ticket_select' : 'product_ticket_unselect'
    "
    :class="{
      'gtm-click': isSelectable,
      'ticket-item--blackout': isBlackedOut,
      'ticket-item--unavailable': isNoLongerAvailable
    }"
    class="ticket-item"
    :lines="blackoutNextAvailableText ? 'three' : ticketSubheading || ticketAttribute ? 'two' : undefined"
    @click="$emit(isSelectable ? 'select-item' : '', orderProduct.id)"
  >
    <template #title><span class="ticket-item__title" :class="{ 'ticket-item__title--blackout': isBlackedOut }">{{ ticketHeading }}</span></template>
    <template v-if="ticketSubheading || ticketAttribute" #subtitle>
      <div style="opacity: 0.7" class="ticket-item__subtitle">
        <div>{{ ticketAttribute }}</div>
        <div
          class="font-weight-bold"
          :class="{ 'ticket-item__blackout-message': isBlackedOut }"
        >{{ ticketSubheading }}</div>
        <div
          v-for="(line, index) in blackoutLines"
          :key="index"
          class="font-weight-bold"
        >{{ line }}</div>
        <div v-if="blackoutNextAvailableText">{{ blackoutNextAvailableText }}</div>
      </div>
    </template>
    <template #append>
      <!-- Redeemed -->
      <div v-if="isRedeemed" class="ticket-item__append ticket-item__date text-body-2 my-2">
        <span class="d-block text-end">{{ $getLocalDate(orderProduct.redeemed, 'MMM D, YYYY') }}</span>
        <span class="text-caption text-grey darken-1 d-block text-end">
          {{ $getLocalDate(orderProduct.redeemed, 'h:mma') }}
        </span>
      </div>
      <!-- No Redemptions Remaining -->
      <div v-else-if="!hasRedemptionsRemaining" class="ticket-item__append"></div>
      <!-- Not started -->
      <div v-else-if="!hasStarted" class="ticket-item__append">
        <icon :key="`add-${orderProduct.id}`" fill="grey" height="24" width="24">
          calendar
        </icon>
      </div>
      <!-- Blacked Out -->
      <div v-else-if="isBlackedOut" class="ticket-item__append"></div>
      <!-- Expired -->
      <div v-else-if="isExpired" class="ticket-item__append ticket-item__date text-body-2 my-2">
        <span class="d-block text-end">{{ $getLocalDate(orderProduct.end_date, 'MMM D, YYYY') }}</span>
        <span class="text-caption text-grey darken-1 d-block text-end">
          {{ $getLocalDate(orderProduct.end_date, 'h:mma') }}
        </span>
      </div>
      <!-- Unredeemed -->
      <div v-else class="ticket-item__append my-2">
        <v-btn
          id="product_redemption_btn"
          :class="`ticket-item__btn ${getButtonClass} my-1`"
          :color="getButtonColor"
          depressed
          fab
          size="32"
        >
          <template v-if="shouldShowCheckIcon">
            <icon :key="`check-${orderProduct.id}`" fill="white" height="18">
              check-mark
            </icon>
          </template>
          <template v-if="shouldShowAddIcon">
            <icon :key="`add-${orderProduct.id}`" fill="white" height="18">
              plus
            </icon>
          </template>
        </v-btn>
      </div>
    </template>
  </v-list-item>
</template>

<script>
import { mapGetters } from 'vuex';
import ticketHelpers from '../helpers';

export default {
  name: 'Ticket',
  props: {
    orderProduct: {
      type: Object,
      default: () => ({})
    },
    product: {
      type: Object,
      default: () => ({})
    },
    itemsSelected: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapGetters('orderProducts', {
      orderProductHasRedemptionsRemaining: 'hasRedemptionsRemaining'
    }),
    ...mapGetters('orderDeals', ['isBlockedByPackageCap']),
    ...mapGetters('deals', { findDeal: 'find' }),

    deal() {
      const dealId = parseInt(this.$route.params.dealId, 10);
      if (!dealId) return null;
      return this.findDeal(dealId) || null;
    },

    dateFormat() {
      const timeRequired = ticketHelpers.schedulingWithTimeRequired(
        this.product
      );
      return `MMMM Do, YYYY${timeRequired ? ', [at] h:mma' : ''}`;
    },

    getButtonColor() {
      if (!this.hasStarted) return 'grey';
      if (this.isSelected) return 'success';
      if (this.isRedeemed) return 'success';
      return 'primary';
    },

    getButtonClass() {
      return this.orderProduct.redeemed
        ? ' items-modal__item__btn--redeemed'
        : '';
    },

    hasStarted() {
      return ticketHelpers.hasStarted(this.orderProduct, this.product);
    },

    isSelectable() {
      return (
        this.hasRedemptionsRemaining &&
        ticketHelpers.isSelectable(this.orderProduct, this.product, this.deal)
      );
    },

    isBlackedOut() {
      return ticketHelpers.isBlackedOut(
        this.orderProduct,
        this.product,
        this.deal
      );
    },

    isDateSpecific() {
      return ticketHelpers.isDateSpecific(this.product);
    },

    // This ticket is blocked specifically by the activity's spent Max Redemptions cap.
    //
    // A ticket that was already redeemed is a record of use rather than something unavailable —
    // including a booking redeemed for a future date, which must keep reading "Coming Soon" with its
    // "For use on" date. An expired one is unusable for a reason of its own that is more useful to
    // show than the cap.
    //
    // Everything that reacts to the cap keys off this one computed, so the heading, the append slot,
    // selectability and the dim can't drift apart.
    isNoLongerAvailable() {
      return (
        (!this.hasProductRedemptionsRemaining ||
          this.isBlockedByPackageCap(this.orderProduct.order_deal_id)) &&
        !ticketHelpers.isRedeemed(this.orderProduct) &&
        !this.isExpired
      );
    },

    isExpired() {
      return ticketHelpers.isExpired(this.orderProduct);
    },

    isRedeemed() {
      // A booking redeemed for a future date/time should display as upcoming
      // ("For use on"), not as already redeemed.
      return (
        ticketHelpers.isRedeemed(this.orderProduct) &&
        !ticketHelpers.isFutureRedemption(this.orderProduct)
      );
    },

    isSelected() {
      return this.itemsSelected.includes(this.orderProduct.id);
    },

    nextAvailableDate() {
      return ticketHelpers.nextAvailableDate(
        this.orderProduct,
        this.product,
        this.deal
      );
    },

    hasRedemptionsRemaining() {
      return (
        !this.isNoLongerAvailable &&
        this.orderProductHasRedemptionsRemaining(this.orderProduct)
      );
    },

    hasProductRedemptionsRemaining() {
      return ticketHelpers.hasProductRedemptionsRemaining(this.product);
    },

    productType() {
      return this.product.type || '';
    },

    shouldShowAddIcon() {
      return !this.itemsSelected.includes(this.orderProduct.id);
    },

    shouldShowCheckIcon() {
      return this.itemsSelected.includes(this.orderProduct.id);
    },

    startDate() {
      return ticketHelpers.startDate(this.orderProduct);
    },

    ticketHeading() {
      // TODO: determined again, based on product type
      // When we know the product type we can use (Admission Ticket, Coupon, etc.)
      if (this.isRedeemed) return `${this.product.language_tags.REDEEMED} On:`;
      // The pass ran out of DIFFERENT deals it may use, so this one is not refused for lack of
      // redemptions of its own - saying so would send the holder looking in the wrong place.
      // Kept above the generic "No Longer Available" so the two refusals stay distinguishable
      // even though the package cap now also feeds isNoLongerAvailable (the dim/selectability).
      if (this.isBlockedByPackageCap(this.orderProduct.order_deal_id))
        return 'Experience Limit Reached';
      if (this.isNoLongerAvailable) return 'No Longer Available';
      if (!this.hasRedemptionsRemaining) return 'No Redemptions Remaining';
      if (!this.hasStarted) return 'Coming Soon';
      if (this.isBlackedOut) return 'Item Unavailable';
      if (this.isExpired) return 'Expired On:';
      if (this.productType === 'coupon')
        return this.isSelected ? 'Coupon Selected' : 'Select Coupon';
      if (this.productType === 'admission')
        return this.isSelected ? 'Ticket Selected' : 'Admission Ticket';
      return this.isSelected ? 'Selected' : 'Click to Select';
    },

    ticketSubheading() {
      if (!this.hasStarted && !this.isDateSpecific)
        return `Available starting: ${this.$getLocalDate(this.startDate)}`;

      if (this.isDateSpecific && this.startDate)
        return `For use on: ${this.$getLocalDate(this.startDate, this.dateFormat)}`;

      if (this.isBlackedOut) return this.blackoutContent.message;
      return '';
    },

    // { message, lines } for the blackout card - "Not valid on:" plus one line
    // per blackout date/range/rule, or the partner's custom message.
    blackoutContent() {
      return this.isBlackedOut
        ? ticketHelpers.blackoutUnavailableContent(this.product, this.deal)
        : { message: '', lines: [] };
    },

    // Only shown when the subheading is the blackout message - a date specific
    // ticket keeps its "For use on" subheading instead (see ticketSubheading).
    blackoutLines() {
      if (this.isDateSpecific && this.startDate) return [];
      return this.blackoutContent.lines;
    },

    // Dynamic "Available again starting: {date}" line, shown beneath the blackout
    // message unless the partner has turned it off (settings.blackout_show_next_available).
    blackoutNextAvailableText() {
      if (!this.isBlackedOut || !this.nextAvailableDate) return '';
      if (this.product?.settings?.blackout_show_next_available === false) return '';
      return `Available again starting: ${this.$getLocalDate(this.nextAvailableDate)}`;
    },

    ticketAttribute() {
      const { attribute } = this.orderProduct;

      if (attribute?.id > 0) {
        return `${attribute?.name !== null ? attribute.name : ''}${
          attribute?.sub_title ? ` (${attribute.sub_title})` : ''
        }`;
      }

      return '';
    },

    ticketType() {
      return this.orderProduct?.ticket?.type || '';
    }
  },
  methods: {}
};
</script>

<style lang="scss" scoped>
.items-modal {
  border-radius: 1rem;

  &__item {
    &__redeemed-date {
      display: block;
      font-size: 0.8rem;
    }

    &__btn {
      &--redeemed {
        opacity: 0.4;
      }
    }
  }
}

.v-list-item {
  padding-top: 0px !important;
  padding-bottom: 0px !important;

  &:deep(> .v-ripple__container) {
    display: none;
  }

  &:deep(.v-list-item__content) {
    padding: 8px 0  !important;
  }

  &:deep(.v-list-item-title) {
    font-size: 0.9rem !important;
    white-space: normal;
    letter-spacing:0 !important;
    line-height: 1.2;
  }

  &:deep(.v-list-item-subtitle) {
    font-size: 0.75rem;
  }

  // When blacked out, the title ("Item Unavailable") shares the message's size so
  // the heading + "Not valid on…" + "Available again…" read as one cohesive block.
  &:deep(.v-list-item-title) .ticket-item__title--blackout {
    font-size: 0.75rem !important;
    font-weight: 700;
  }

  // The blackout message length is unbounded (grows with the number of rules, or
  // a partner override), so the fixed line clamp would clip both the message tail
  // and the "Available again…" line — let the subtitle grow instead.
  &.ticket-item--blackout :deep(.v-list-item-subtitle) {
    -webkit-line-clamp: unset;
  }

  // Keep the line breaks a partner types into a custom blackout message.
  .ticket-item__blackout-message {
    white-space: pre-line;
  }

  &:deep(.v-list-item--disabled) .v-list-item-title {
    color: rgba(0, 0, 0, 0.38);
  }
}

// Greyed out so a spent activity reads as gone at a glance, not merely relabelled. Opacity rather
// than a colour override because the rule below forces the list item's colour back to full contrast.
.ticket-item--unavailable {
  opacity: 0.5;
  cursor: default;
}

.ticket-item__append {
  align-self: center;
  display: flex;
  align-items: center;
}

.ticket-item__date {
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  white-space: nowrap;
  text-align: end;
}

:deep(.v-list-item:not(.v-list-item--active):not([aria-disabled="true"])) {
  color: rgba(0, 0, 0, 0.87);
}

// :deep(.v-list-item--density-default.v-list-item--one-line) {
//   padding-top: 0px !important;
//   padding-bottom: 0px !important;
// }
// :deep(.v-list-item--density-default.v-list-item--two-line) {
//   padding-top: 0px !important;
//   padding-bottom: 0px !important;
// }

@media (max-width: 490px) {
  :deep(.v-list-item-title) {
    line-height: 1.5 !important;
  }
}

</style>
